import { isDisplayableImage, normalizeDates } from '@/lib/remote-data/normalize'
import { getCache } from '@/lib/cache'
import { fetchRdbmsJson } from '@/lib/rdbms-api'

type ApiDateRecord = {
  value: string
  precision: string
}

type ApiSearchItem = {
  id: string
  label: string
  image?: string | null
  datesOfBirth: ApiDateRecord[]
  datesOfDeath: ApiDateRecord[]
  occupations: string[]
}

type ApiSearchResponse = {
  page: number
  limit: number
  total: number
  hasMore: boolean
  items: ApiSearchItem[]
}

const cacheDuration = 1000 * 60 * 60 * 12 // 12 hours

export const useFilterResultsStore = defineStore('filterResults', () => {
  const limit = ref(30)
  const page = ref(1)
  const done = ref(true)

  const route = useRoute()
  const filterInputStore = useFilterInputStore()
  const envStore = useEnvStore()

  const shouldFetch = computed(() => route.name === 'list')
  const shouldCache = computed(() => !envStore.isLoggedInUser)

  // Keep API payload inspectable in debug UIs.
  const basePayload = computed(() => {
    const filters = JSON.parse(JSON.stringify(filterInputStore.values))
    const active = JSON.parse(JSON.stringify(filterInputStore.isOptionActive))
    return {
      filters,
      active,
      sorting: filterInputStore.values.sorting,
    }
  })

  const query = computed(() =>
    JSON.stringify({
      ...basePayload.value,
      page: page.value,
      limit: limit.value,
    }),
  )

  const cache = getCache({ duration: cacheDuration })

  const data = ref<
    Array<{
      id: string
      label: string
      image?: string
      datesOfBirth: Date[]
      datesOfDeath: Date[]
      occupations: string[]
    }>
  >([])

  const error = ref<unknown>()
  const isFetching = ref(false)
  const isAborted = ref(false)

  let controller: AbortController | undefined

  function normalizeSearchItem(item: ApiSearchItem) {
    const dateOfBirthValues = item.datesOfBirth.map(entry => entry.value)
    const dateOfBirthPrecision = item.datesOfBirth.map(entry => entry.precision)
    const dateOfDeathValues = item.datesOfDeath.map(entry => entry.value)
    const dateOfDeathPrecision = item.datesOfDeath.map(entry => entry.precision)

    return {
      id: item.id,
      label: item.label,
      image: isDisplayableImage(item.image ?? '') ? item.image ?? undefined : undefined,
      datesOfBirth: normalizeDates(dateOfBirthValues, dateOfBirthPrecision),
      datesOfDeath: normalizeDates(dateOfDeathValues, dateOfDeathPrecision),
      occupations: [...new Set(item.occupations.filter(Boolean))],
    }
  }

  async function fetchResults() {
    if (!shouldFetch.value) return

    try {
      controller?.abort()
    } catch {}

    controller = new AbortController()
    const requestController = controller

    isFetching.value = true
    error.value = undefined
    isAborted.value = false

    const payload = {
      ...basePayload.value,
      page: page.value,
      limit: limit.value,
    }

    try {
      let response: ApiSearchResponse | undefined
      const cacheKey = shouldCache.value
        ? await cache.makeKey('rdbms-search', JSON.stringify(payload))
        : undefined

      if (cacheKey && (await cache.has(cacheKey))) {
        response = await cache.get(cacheKey)
      }

      if (!response) {
        response = await fetchRdbmsJson<ApiSearchResponse>('search', {
          method: 'POST',
          signal: requestController.signal,
          body: payload,
        })

        if (cacheKey) {
          await cache.set(cacheKey, response)
        }
      }

      if (requestController !== controller) return

      const mapped = response.items.map(normalizeSearchItem)
      if (page.value === 1) {
        data.value = mapped
      } else {
        data.value.push(...mapped)
      }

      done.value = !response.hasMore
    } catch (exception: any) {
      if (exception?.name === 'AbortError') {
        isAborted.value = true
        return
      }

      error.value = exception
    } finally {
      if (requestController === controller) {
        isFetching.value = false
      }
    }
  }

  watch(
    () => JSON.stringify(basePayload.value),
    () => {
      page.value = 1
    },
  )

  watch(
    [() => JSON.stringify(basePayload.value), page, shouldFetch],
    () => {
      void fetchResults()
    },
    { immediate: true },
  )

  return {
    query,
    isFetching,
    error: computed(() => (String(error.value).includes('aborted') ? undefined : error.value)),
    data,
    hasMore: computed(() => {
      if (page.value === 1 && isFetching.value) return false
      return !done.value
    }),
    isAborted,
    loadMore() {
      if (done.value) return
      page.value++
    },
  }
})
